import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Label from "../../components/Common/Label";
import AnalysisIcon from "../../assets/images/analysis.svg";
import TrueIcon from "../../assets/images/trueAnalysis.svg";
import CompanyLogo from "../../assets/images/Logo_Icon.svg";
import ChannelAnalysisIcon from "../../assets/images/channelAnalysisIcon.svg";
import { Grid, useMediaQuery, Box, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SocialMediaIcon from "../../components/SocialMediaIcon";
import Loader from "../../utils/Loading";
import { getRequest, postRequest } from "../../api/apiServices";
import apiList from "../../api/apiList";
import {
  Analytical,
  HistogramData,
  TopCompany,
  RegionalData,
} from "../../utils/AnalytivalDataType";
import HistoricalChart from "../../components/AnalyticsChart/HistoricalChart";
import HistogramChart from "../../components/AnalyticsChart/HistogramChart";
import RegionalChart from "../../components/AnalyticsChart/RegionalChart";
import TopCompaniesChart from "../../components/AnalyticsChart/TopCompaniesChart";
import ChartSkeleton from "../../components/AnalyticsChart/ChartSkeleton";
import ChartEmptyState from "../../components/AnalyticsChart/ChartEmptyState";
import ChartDescriptions from "../../components/Popups/ChartDescription";

const Analytics: React.FC = () => {
  const [formData, setFormData] = useState<Analytical>();
  const [historicalData, setHistoricalData] = useState<{
    [key: string]: number;
  } | null>(null);
  const [histogramData, setHistogramData] = useState<HistogramData | null>(
    null
  );
  const [regionalData, setRegionalData] = useState<RegionalData[] | null>(null);
  const [topCompaniesData, setTopCompaniesData] = useState<TopCompany[] | null>(
    null
  );

  // Individual loading states for each chart
  const [loadingHistorical, setLoadingHistorical] = useState(true);
  const [loadingHistogram, setLoadingHistogram] = useState(true);
  const [loadingRegional, setLoadingRegional] = useState(true);
  const [loadingTopCompanies, setLoadingTopCompanies] = useState(true);

  const [openDescriptionPopup, setOpenDescriptionPopup] = useState(false);
  const [description, setDescription] = useState("");
  const location = useLocation();
  const jobTitle = location.state?.jobTitle || "";
  const navigate = useNavigate();
  const [loaing, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width:1200px)");
  const isTablet = useMediaQuery("(max-width:900px)");

  //Get Data
  const getAnalyticsData = async () => {
    setLoading(true);
    const response = await postRequest(apiList.getAnalytics, {
      name: jobTitle,
    });
    if (response) {
      setFormData(response.data);
      setLoading(false);
    }
    setLoading(false);
  };

  const fetchHistoricalData = async () => {
    setLoadingHistorical(true);
    try {
      const response = await getRequest(apiList.getHistoricalChart);
      if (response?.data.success) {
        setHistoricalData(response?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching historical data:", error);
    } finally {
      setLoadingHistorical(false);
    }
  };

  const fetchHistogramData = async () => {
    setLoadingHistogram(true);
    try {
      const response = await postRequest(apiList.getHistogramChart, {
        name: jobTitle,
      });
      if (response?.data.success) {
        setHistogramData(response?.data.data);
      }
    } catch (error) {
      console.error("Error fetching histogram data:", error);
    } finally {
      setLoadingHistogram(false);
    }
  };

  const fetchRegionalData = async () => {
    setLoadingRegional(true);
    try {
      const response = await getRequest(apiList.getRegionalChart);
      if (response?.data.success) {
        setRegionalData(response?.data.data);
      }
    } catch (error) {
      console.error("Error fetching regional data:", error);
    } finally {
      setLoadingRegional(false);
    }
  };

  const fetchTopCompaniesData = async () => {
    setLoadingTopCompanies(true);
    try {
      const response = await postRequest(apiList.getTopCompaniesChart, {
        name: jobTitle,
      });
      if (response?.data.success) {
        setTopCompaniesData(response?.data.data);
      }
    } catch (error) {
      console.error("Error fetching top companies data:", error);
    } finally {
      setLoadingTopCompanies(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      // Helper function to add delay to prevent rate limiting
      const delay = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms));

      // Call APIs sequentially with 2-second delays to avoid rate limiting
      await fetchHistoricalData();
      await delay(2000);

      await fetchHistogramData();
      await delay(2000);

      await fetchRegionalData();
      await delay(2000);

      await fetchTopCompaniesData();
    };

    fetchAllData();
    getAnalyticsData();
  }, []);

  //Navigate Charts
  const viewCharts = (description: string) => {
    setDescription(description);
    setOpenDescriptionPopup(true);
  };

  return (
    <>
      {/* Back Button */}
      <Box
        sx={{
          padding: { xs: 2, sm: 3, md: 4 },
          paddingBottom: 0,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: "#ffffff",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              backgroundColor: "#84E1CC",
              "& svg": { color: "#fff" },
            },
          }}
        >
          <ArrowBackIcon sx={{ color: "#84E1CC" }} />
        </IconButton>
      </Box>

      <div
        className="container-fluid"
        style={{
          paddingTop: "20px",
          paddingBottom: "50px",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          minHeight: "100vh",
        }}
      >
        <div className="row d-flex align-items-center justify-content-start flex-column gap-4">
          {/* Job title header with charts */}
          <Grid
            container
            spacing={3}
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: "24px",
              border: "1px solid #e5e7eb",
              padding: { xs: 2, sm: 3, md: 4 },
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              maxWidth: "1400px",
              margin: "0 auto",
            }}
          >
            <Grid item xs={12}>
              <Label
                children={jobTitle}
                fontSize={isMobile ? "18px" : "24px"}
                fontWeight={700}
                color="#273848"
              />
            </Grid>

            {/* Historical Chart */}
            <Grid item xs={12} md={6}>
              {loadingHistorical ? (
                <ChartSkeleton variant="line" height={isMobile ? 250 : 320} />
              ) : historicalData && Object.keys(historicalData).length > 0 ? (
                <HistoricalChart data={historicalData} />
              ) : (
                <ChartEmptyState
                  message="Historical Data Unavailable"
                  height={isMobile ? 250 : 320}
                />
              )}
            </Grid>

            {/* Histogram Chart */}
            <Grid item xs={12} md={6}>
              {loadingHistogram ? (
                <ChartSkeleton variant="bar" height={isMobile ? 250 : 320} />
              ) : histogramData && Object.keys(histogramData).length > 0 ? (
                <HistogramChart data={histogramData} jobTitle={jobTitle} />
              ) : (
                <ChartEmptyState
                  message="Histogram Data Unavailable"
                  height={isMobile ? 250 : 320}
                />
              )}
            </Grid>

            {/* Regional Chart */}
            <Grid item xs={12} md={6}>
              {loadingRegional ? (
                <ChartSkeleton
                  variant="doughnut"
                  height={isMobile ? 350 : 420}
                />
              ) : regionalData && regionalData.length > 0 ? (
                <RegionalChart data={regionalData} jobTitle={jobTitle} />
              ) : (
                <ChartEmptyState
                  message="Regional Data Unavailable"
                  height={isMobile ? 350 : 420}
                />
              )}
            </Grid>

            {/* Top Companies Chart */}
            <Grid item xs={12} md={6}>
              {loadingTopCompanies ? (
                <ChartSkeleton
                  variant="horizontal-bar"
                  height={isMobile ? 320 : 420}
                />
              ) : topCompaniesData && topCompaniesData.length > 0 ? (
                <TopCompaniesChart data={topCompaniesData} />
              ) : (
                <ChartEmptyState
                  message="Top Companies Data Unavailable"
                  height={isMobile ? 320 : 420}
                />
              )}
            </Grid>
          </Grid>

          {loaing ? (
            <Loader />
          ) : (
            <>
              {/* Top Jobs Cards */}
              <Grid
                container
                spacing={2}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "24px",
                  padding: { xs: 2, sm: 3, md: 4 },
                  backgroundColor: "white",
                  boxShadow:
                    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                  maxWidth: "1400px",
                  margin: "0 auto",
                }}
              >
                <Grid item xs={12}>
                  <Label
                    children="Top 4 jobs"
                    fontWeight={700}
                    fontSize={isMobile ? "18px" : "22px"}
                    color="#273848"
                  />
                </Grid>

                {formData && formData?.data?.jobDetails ? (
                  formData?.data?.jobDetails
                    ?.filter((data) => data !== null)
                    ?.map((data, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <div
                          className="custom-card"
                          style={{
                            cursor: "pointer",
                            height: "100%",
                            transition:
                              "transform 0.3s ease, box-shadow 0.3s ease",
                            borderRadius: "16px",
                            border: "1px solid #e5e7eb",
                            overflow: "hidden",
                          }}
                          onClick={() => viewCharts(data?.description)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-8px)";
                            e.currentTarget.style.boxShadow =
                              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)";
                          }}
                        >
                          <div
                            className="card-content"
                            style={{ padding: "20px" }}
                          >
                            <h4
                              className="company-name"
                              style={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: "#a5b4fc",
                                marginBottom: "8px",
                              }}
                            >
                              {data?.company}
                            </h4>
                            <h5
                              className="job-title"
                              style={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#ffffff",
                                marginBottom: "8px",
                              }}
                            >
                              {data?.title}
                            </h5>
                            <p
                              className="job-location"
                              style={{
                                fontSize: "13px",
                                color: "#e0e7ff",
                                marginBottom: "4px",
                              }}
                            >
                              {data?.location}
                            </p>
                            <p
                              className="job-salary"
                              style={{
                                fontSize: "13px",
                                color: "#86efac",
                                fontWeight: 600,
                              }}
                            >
                              {data?.salary}
                            </p>
                          </div>

                          <div
                            className="card-footer"
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                            }}
                          >
                            <span
                              className="footer-text"
                              style={{
                                fontSize: "12px",
                                color: "#ffffff",
                                fontWeight: 500,
                              }}
                            >
                              Click for more details
                            </span>
                          </div>
                        </div>
                      </Grid>
                    ))
                ) : (
                  <Grid item xs={12}>
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <p style={{ color: "#94a3b8", fontSize: "16px" }}>
                        No Jobs Found
                      </p>
                    </div>
                  </Grid>
                )}
              </Grid>

              {/* Approach Analysis */}
              <Grid
                container
                spacing={2}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "24px",
                  padding: { xs: 2, sm: 3, md: 4 },
                  backgroundColor: "white",
                  boxShadow:
                    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                  maxWidth: "1400px",
                  margin: "0 auto",
                }}
              >
                <Grid item xs={12} display="flex" alignItems="center" gap={2}>
                  <img src={AnalysisIcon} alt="Analysis-Icon" />
                  <Label
                    children="Approach Analysis"
                    fontWeight={700}
                    fontSize={isMobile ? "18px" : "22px"}
                    color="#273848"
                  />
                </Grid>

                {formData?.data &&
                formData?.data?.approchAnalysisData?.length > 0 ? (
                  formData.data.approchAnalysisData
                    .slice(0, 4)
                    ?.map((data, index) => (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        key={index}
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                      >
                        <img
                          src={TrueIcon}
                          alt="True-Icon"
                          style={{ marginTop: "4px" }}
                        />
                        <Label
                          fontWeight={500}
                          fontSize={"14px"}
                          color="#273848"
                        >
                          {data}
                        </Label>
                      </Grid>
                    ))
                ) : (
                  <Grid item xs={12}>
                    <p style={{ color: "#94a3b8", fontSize: "14px" }}>
                      No data found
                    </p>
                  </Grid>
                )}
              </Grid>

              {/* Channel Analysis */}
              <Grid
                container
                spacing={2}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "24px",
                  padding: { xs: 2, sm: 3, md: 4 },
                  backgroundColor: "white",
                  boxShadow:
                    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                  maxWidth: "1400px",
                  margin: "0 auto",
                  marginBottom: "20px",
                }}
              >
                <Grid item xs={12} display="flex" alignItems="center" gap={2}>
                  <img src={ChannelAnalysisIcon} alt="ChannelAnalysis-Icon" />
                  <Label
                    children="Channels Analysis"
                    fontWeight={700}
                    fontSize={isMobile ? "18px" : "22px"}
                    color="#273848"
                  />
                </Grid>

                <Grid item xs={12}>
                  <SocialMediaIcon
                    names={formData?.data?.channelAnalysisData || []}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </div>
      </div>

      {openDescriptionPopup && (
        <ChartDescriptions
          open={openDescriptionPopup}
          description={description}
          onClose={() => setOpenDescriptionPopup(false)}
        />
      )}
    </>
  );
};

export default Analytics;
